import {Color, ChartType, ChartDataset, ScriptableContext} from 'chart.js';
import chroma from 'chroma-js'; // Chroma comes w/ `chartjs-chart-funnel`

/**
 * Strictly adhere to the following, regardless of the designs
 * @see {@link https://www.ibm.com/design/language/data-visualization/design/basics/}
 * @see {@link https://carbondesignsystem.com/data-visualization/color-palettes/}
 * @see {@link https://getbootstrap.com/docs/5.3/customize/color/#all-colors}
 * For another great example @see {@link https://design.gitlab.com/data-visualization/color}
 */

export const getColour = (html: HTMLHtmlElement, bsColour: string): Color => {
  return window.getComputedStyle(html).getPropertyValue(bsColour);
};

// 100 to 900
const getBsColours = (html: HTMLHtmlElement, color: string) =>
  [...new Array(9)].map((_, i) => getColour(html, `--bs-${color}-${i + 1}00`));

/**
 * Okabe-Ito, the standard colourblind-safe categorical palette.
 * @see {@link https://jfly.uni-koeln.de/color/}
 * Black is swapped for white on dark backgrounds.
 */
const OKABE_ITO = ['#E69F00', '#56B4E9', '#009E73', '#F0E442', '#0072B2', '#D55E00', '#CC79A7'];

/** Shared by Chart.js & the PowerBI themes, so that both look the same. */
export const getColourblindPalette = (dark: boolean): string[] => [
  ...OKABE_ITO,
  dark ? '#FFFFFF' : '#000000',
];

export const isColourblind = (): boolean => document.documentElement.dataset.colorblind === 'true';

/**
 * Sequential data uses steps within a single hue to present a single data range.
 * Categorical data uses hues to differentiate categories, and like sequential data, steps within each hue to present a data range.
 * Divergent data uses hue transitions to present data ranges on either side of a common central point.
 */

/**
 * Categorical (or qualitative) palettes are best when you want to distinguish discrete categories of data that do not have an inherent correlation.
 * This is a custom palette from product/design. It builds on IBM's categorical palette.
 * @see {@link https://www.figma.com/design/CLFn9pCQth5uE5pOOfXvwA/Sphere-Design-System}
 */
const getCategoricalPalette = (): Readonly<Color[]> => {
  const html = document.querySelector('html') as HTMLHtmlElement;
  const dark = html.getAttribute('data-bs-theme') === 'dark';
  if (isColourblind()) return getColourblindPalette(dark);
  const lightPalette = [
    '#6929C4',
    '#1192E8',
    '#005D5D',
    '#9F1853',
    '#FA4D56',
    '#520408',
    '#198038',
    '#002D9C',
    '#EE5396',
    '#B28600',
    '#009D9A',
    '#012749',
    '#8A3800',
    '#A56EFF',
    '#FF832B',
    '#31135E',
    '#F1C21B',
    '#0F62FE',
    '#DA1E28',
    '#42BE65',
    '#0072C3',
  ];
  const darkPalette = [
    '#8A3FFC',
    '#33B1FF',
    '#007D79',
    '#FF7EB6',
    '#FA4D56',
    '#FFF1F1',
    '#6FDC8C',
    '#4589FF',
    '#D12771',
    '#D2A106',
    '#08BDBA',
    '#BAE6FF',
    '#BA4E00',
    '#D4BBFF',
    '#FF832B',
    '#A56EFF',
    '#F1C21B',
    '#78A9FF',
    '#FF8389',
    '#A7F0BA',
    '#1192E8',
  ];
  return dark === true ? darkPalette : lightPalette;
};

/**
 * Custom palette from product/design. Intended to solve labelling issues between security datasets.
 * @see {@link https://www.figma.com/design/CLFn9pCQth5uE5pOOfXvwA/Sphere-Design-System}
 * e.g Low, Medium, High
 * e.g Success, In Progress, Failure
 */
export const getTrafficPalette = (): Readonly<Color[]> => {
  const html = document.querySelector('html') as HTMLHtmlElement;
  const dark = html.getAttribute('data-bs-theme') === 'dark';
  // Low to high: sky blue, yellow, orange, vermillion
  if (isColourblind()) return [OKABE_ITO[1], OKABE_ITO[3], OKABE_ITO[0], OKABE_ITO[5]];
  const lightPalette = [
    // '#006164', This is not yet used by any chart designs, see RND-2771
    '#57C4AD',
    '#E6E1BC',
    '#EDA247',
    '#DB4325',
  ];
  const darkPalette = [
    // '#489F76',
    '#B5CE3F',
    '#ECC349',
    '#FD9843',
    '#E25E6B',
  ];
  return dark === true ? darkPalette : lightPalette;
};

/**
 * The monochromatic palettes are good for relationship charts and trend charts.
 * In light themes, the darkest color denotes the largest values.
 * In dark themes, the lightest color denotes the largest values.
 */
export const getSequentialMonochromaticPalette = () => {
  const html = document.querySelector('html') as HTMLHtmlElement;
  const dark = html.getAttribute('data-bs-theme') === 'dark';
  const colours = [
    getBsColours(html, 'blue'),
    getBsColours(html, 'indigo'),
    getBsColours(html, 'cyan'),
    getBsColours(html, 'teal'),
  ];
  return dark === true ? colours.map((c) => c.reverse()) : colours;
};

/**
 * Note that diverging palettes do not differentiate between light and dark themes.
 */

/**
 * The red-cyan palette has a natural association with temperature.
 * Use this palette for data representing hot-vs-cold.
 */
export const getSequentialDivergingTemperaturePalette = () => {
  const html = document.querySelector('html') as HTMLHtmlElement;
  return [getBsColours(html, 'red'), getBsColours(html, 'blue')];
};

// The purple-teal palette is good for data with no temperature associations, such as performance, sales, and rates of change.
export const getSequentialDivergingPalette = () => {
  const html = document.querySelector('html') as HTMLHtmlElement;
  return [getBsColours(html, 'indigo'), getBsColours(html, 'teal')];
};

// For some reason, management wants "greener greens" for the gauge... >:[

export interface GaugeColours {
  blue: Color;
  red: Color;
  orange: Color;
  yellow: Color;
  green: Color;
}

export const getGaugeColours = (): Readonly<GaugeColours> => {
  if (isColourblind())
    return {
      blue: OKABE_ITO[4],
      red: OKABE_ITO[5],
      orange: OKABE_ITO[0],
      yellow: OKABE_ITO[3],
      green: OKABE_ITO[4], // No green, blue is the colourblind-safe "good"
    };
  const palette = getCategoricalPalette();
  return {
    blue: palette[1],
    red: palette[18],
    orange: palette[14],
    yellow: palette[16],
    green: palette[19],
  };
};

/**
 * `chartjs-chart-funnel` requires it's own palette
 * @see {@link https://www.sgratzl.com/chartjs-chart-funnel/examples/gradient.html}
 * @see {@link https://gka.github.io/chroma.js/#color-scales}
 */
export const getFunnelColour = (n: number) => {
  const html = document.querySelector('html') as HTMLHtmlElement;
  const dark = html.getAttribute('data-bs-theme') === 'dark';
  const teal = dark === true ? ['#1AA179', '#0D503C'] : ['#D2F4EA', '#4DD4AC'];
  const blue = dark === true ? ['#56B4E9', '#003B5C'] : ['#CDE9F9', '#0072B2'];
  const colours = isColourblind() ? blue : teal;
  const scale = chroma.scale(colours).colors(n); // Provided by RND-2771
  const backgroundColor = scale.map((s) => `rgb(${chroma(s).rgb()})`);
  return {
    backgroundColor: backgroundColor,
    shrinkAnchor: 'bottom',
    borderColor: 'transparent',
  }; // https://www.sgratzl.com/chartjs-chart-funnel/examples/shrink.html
};

/**
 * `chartjs-chart-sankey` requires it's own palette
 * @see {@link https://github.com/kurkle/chartjs-chart-sankey}
 */
const getSankeyColours = <T extends ChartType>(dataset: ChartDataset<T>) => {
  const html = document.querySelector('html') as HTMLHtmlElement;
  // Sankey chart should only have one dataset
  const points = dataset.data.flatMap((d: any) => {
    // Any required, some properties do not exist on all generic types T
    return [d.from, d.to];
  });
  const uniquePoints = [...new Set(points)];
  // Create a label -> colour map as per the sankey plugin docs
  const colourMap = new Map();
  for (const [i, k] of uniquePoints.entries()) {
    const c = getCategoricalPalette()[i] || getCategoricalPalette()[0];
    colourMap.set(k, c);
  }
  // Return sankey plugin dataset options
  return {
    nodeWidth: 0,
    borderWidth: 8,
    color: getColour(html, '--bs-body-color'),
    borderColor: getColour(html, '--bs-body-color'),
    colorFrom: (d: ScriptableContext<'sankey'>) => colourMap.get(d.dataset.data[d.dataIndex]?.from),
    colorTo: (d: ScriptableContext<'sankey'>) => colourMap.get(d.dataset.data[d.dataIndex]?.to),
  };
};

export const getDatasetColours = <T extends ChartType>(
  datasets: any, // Urgent fix
  type: T,
  options: any,
  index: number,
) => {
  const html = document.querySelector('html') as HTMLHtmlElement;
  const dataset = datasets[index];
  if (dataset?.backgroundColor?.length > 0 || dataset?.borderColor?.length > 0) return {};
  switch (type) {
    case 'doughnut':
    case 'pie':
      return {
        backgroundColor: getCategoricalPalette(),
        borderColor: getColour(html, '--bs-body-bg'),
        borderWidth: 1,
      };
    case 'bar':
      // Bar charts may be grouped and stacked
      // ...or might have one grouped dataset, e.g /security/mttr_toolset
      if (datasets.length > 1 || options?.plugins?.htmlLegend?.showSingleDataset) {
        return {
          backgroundColor: getCategoricalPalette()[index],
          borderColor: getCategoricalPalette()[index],
          borderWidth: 0,
        };
      }
      return {
        backgroundColor: getCategoricalPalette(),
        borderColor: getColour(html, '--bs-body-bg'),
        borderWidth: 0,
      };
    case 'line':
      return {
        backgroundColor: getCategoricalPalette()[index],
        borderColor: getCategoricalPalette()[index],
      };
    case 'sankey':
      return getSankeyColours(dataset);
    case 'choropleth':
      return {borderColor: getColour(html, '--bs-border-color')};
    case 'bubbleMap':
      return {
        showOutline: true,
        outlineBorderColor: getColour(html, '--bs-border-color'),
        outlineBackgroundColor: getColour(html, '--bs-border-color'),
      };
    case 'funnel':
      return getFunnelColour(Object.keys(dataset.data).length);
    default:
      return {
        backgroundColor: getCategoricalPalette(),
        borderColor: getCategoricalPalette(),
        borderWidth: 1,
      };
  }
};
